import { Address, FullCity } from "@deligoo/shared"
import { Button, CityField, fetchFindPostalCode, Field, Icon, Input, StreetField } from "@deligoo/ui"
import { debounce } from "lodash"
import { FormEvent, useEffect, useRef } from "react"
import NumberFormat from "react-number-format"
import { toast } from "react-toastify"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { selectClients } from "@/store/clients"
import { ManualOrder } from "@/types"
import { handleStandardError } from "@/utils/errors"

import { useManualOrderFormContext } from ".."
import cls from "../AddManualOrderView.module.scss"

export const PickupDataView = () => {
  const clients = useAppSelector(selectClients)

  const { formState, setFormStateValue, setNextStep, setPreviousStep } = useManualOrderFormContext()

  const { company_name, phone, city, state, county, municipality, street, house_number, postal_code } =
    formState.values.pickup_attributes || {}

  useEffect(() => {
    const client = clients.find((client) => client.id === formState.values.client_id)

    const { company_name, phone, city, state, county, municipality, street, house_number, flat_number, postal_code } =
      client?.main_delivery_contact_detail || {}

    setFormStateValue({
      pickup_attributes: {
        company_name,
        phone,
        city,
        state,
        county,
        municipality,
        street,
        house_number,
        flat_number,
        postal_code,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, formState.values.client_id])

  function onInputChange(e: FormEvent<HTMLInputElement>) {
    setFormStateValue({
      pickup_attributes: {
        ...(formState.values.pickup_attributes || {}),
        [e.currentTarget.name]: e.currentTarget.value,
      },
    })
  }

  function submitStep() {
    const { client_id } = formState.values

    let errorsCount = 0

    toast.dismiss("clientIdError")
    toast.dismiss("clientAddressError")

    if (!client_id) {
      errorsCount++

      toast.error("Wybierz lokalizację", {
        toastId: "clientIdError",
        updateId: "clientIdError",
      })
    }

    if (
      !company_name ||
      !phone ||
      !city ||
      !street ||
      !house_number ||
      !postal_code ||
      !state ||
      !county ||
      !municipality
    ) {
      errorsCount++

      toast.error("Uzupełnij adres lokalizacji", {
        toastId: "clientAddressError",
        updateId: "clientAddressError",
      })
    }

    if (!errorsCount) setNextStep()
  }

  async function updatePostalCode(pickupAttributes: ManualOrder["pickup_attributes"]) {
    const { city, street, state, county, municipality, house_number } = pickupAttributes

    const isValid = Boolean(city && street && state && county && municipality && house_number)

    if (!isValid || pickupAttributes.postal_code) return

    const partialAddress = {
      city,
      street,
      state,
      county,
      municipality,
      house_number,
    } as Omit<Address, "postal_code">

    const { response, json } = await fetchFindPostalCode(partialAddress)

    if (json.errors) {
      handleStandardError(response, json)
      return
    }

    if (response.ok && json.data) {
      setFormStateValue({
        pickup_attributes: {
          ...pickupAttributes,
          postal_code: json.data.postal_code,
        },
      })
    }
  }

  const debouncedUpdatePostalCode = useRef(debounce(updatePostalCode, 1000)).current

  useEffect(() => {
    const pickupAttributes = formState.values.pickup_attributes

    if (pickupAttributes) {
      debouncedUpdatePostalCode(pickupAttributes)
    } else {
      debouncedUpdatePostalCode.cancel()
    }

    return () => {
      debouncedUpdatePostalCode.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, county, debouncedUpdatePostalCode, house_number, municipality, state, street])

  return (
    <Card className={cls.card}>
      <Card.Section>
        <h3>Wprowadź dane odbioru</h3>
      </Card.Section>

      <Card.Section className={cls.pickupData}>
        <Field
          label="Nazwa"
          name="company_name"
          inputProps={{
            value: company_name || "",
            onChange: onInputChange,
          }}
          required
        />

        <Field required label="Numer telefonu" name="phone">
          <NumberFormat
            onChange={onInputChange}
            value={phone || ""}
            customInput={Input}
            format="### ### ###"
            name="phone"
            id="phone"
            size="large"
            plain
            selectOnFocus
          />
        </Field>

        <CityField
          value={city || ""}
          setValue={(value: FullCity) => {
            setFormStateValue({
              pickup_attributes: {
                ...(formState.values.pickup_attributes || {}),
                postal_code: undefined,
                ...value,
              },
            })
          }}
          key={city}
        />

        <StreetField
          value={street || ""}
          cityValue={{
            city: city || "",
            state: state || "",
            county: county || "",
            municipality: municipality || "",
          }}
          setValue={(value: string) => {
            setFormStateValue({
              pickup_attributes: {
                ...(formState.values.pickup_attributes || {}),
                postal_code: undefined,
                street: value,
              },
            })
          }}
          key={street}
        />

        <Field
          required
          label="Numer domu"
          name="house_number"
          inputProps={{
            value: house_number || "",
            onChange: (e) => {
              setFormStateValue({
                pickup_attributes: {
                  ...(formState.values.pickup_attributes || {}),
                  [e.currentTarget.name]: e.currentTarget.value,
                  postal_code: undefined,
                },
              })
            },
          }}
        />

        <Field
          label="Numer mieszkania"
          name="flat_number"
          inputProps={{
            value: formState.values.pickup_attributes?.flat_number || "",
            onChange: onInputChange,
          }}
        />

        <Field required label="Kod pocztowy" name="postal_code">
          <NumberFormat
            format="##-###"
            customInput={Input}
            name="postal_code"
            id="postal_code"
            value={postal_code || ""}
            onChange={onInputChange}
            size="large"
            selectOnFocus
            plain
          />
        </Field>
      </Card.Section>

      <Card.Section className={cls.submitBtns}>
        <Button
          className={cls.submitBtn}
          color="primary"
          variation="accented"
          size="extra-large"
          fullWidth
          onClick={setPreviousStep}
        >
          <Icon name="short-arrow-tiny-left" />
          Wstecz
        </Button>

        <Button className={cls.submitBtn} color="primary" size="extra-large" fullWidth onClick={submitStep}>
          Przejdź dalej
          <Icon name="short-arrow-tiny-right" />
        </Button>
      </Card.Section>
    </Card>
  )
}
